import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GitMerge, Zap, PlusCircle, Bug, ShieldCheck, Rocket } from 'lucide-react';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const changelogData = [
  {
    version: "v2.1.0",
    date: "2025-06-10",
    type: "Feature Release",
    icon: <Rocket className="w-6 h-6 text-green-500" />,
    title: "AI-Powered Document Summarization API (Beta)",
    description: "Introducing the beta version of our new AI Summarization API. Get concise summaries of long PDF documents. Currently supports English language documents up to 50 pages.",
    changes: [
      { type: "New", text: "Added `/v1/summarize` endpoint for AI-driven document summarization." },
      { type: "Improvement", text: "Enhanced processing speed for PDF to Text conversion by 15%." },
      { type: "Fix", text: "Resolved an issue with metadata handling for certain PDF/A compliant files." },
    ],
    tags: ["AI", "New API", "Performance"]
  },
  {
    version: "v2.0.5",
    date: "2025-05-20",
    type: "Maintenance Update",
    icon: <ShieldCheck className="w-6 h-6 text-blue-500" />,
    title: "Security Enhancements & Minor Fixes",
    description: "This update includes important security patches and addresses several minor bugs reported by users.",
    changes: [
      { type: "Security", text: "Updated internal libraries to address potential vulnerabilities (CVE-2025-XXXX, CVE-2025-YYYY)." },
      { type: "Fix", text: "Corrected an issue where watermarks were sometimes misaligned on A3-sized documents." },
      { type: "Fix", text: "Improved error messaging for invalid API key submissions." },
    ],
    tags: ["Security", "Bug Fix"]
  },
  {
    version: "v2.0.0",
    date: "2025-04-15",
    type: "Major Version Release",
    icon: <GitMerge className="w-6 h-6 text-purple-500" />,
    title: "WebFiny API v2 Launch!",
    description: "Major platform upgrade with new APIs, improved performance, and a revamped developer dashboard. Read our migration guide for v1 users.",
    changes: [
      { type: "New", text: "Launched PDF Redaction API (`/v2/redact`)." },
      { type: "New", text: "Introduced PDF Form Filling API (`/v2/fill-form`)." },
      { type: "Improvement", text: "Upgraded core PDF engine for better compatibility with complex PDF structures." },
      { type: "Improvement", text: "New developer dashboard with enhanced analytics and API key management." },
      { type: "Breaking", text: "API v1 endpoints are now deprecated and will be sunset on 2025-10-15. See migration guide." },
    ],
    tags: ["Major Release", "New API", "Performance", "Developer Tools"]
  },
  {
    version: "v1.8.2",
    date: "2025-03-01",
    type: "Improvement Release",
    icon: <Zap className="w-6 h-6 text-yellow-500" />,
    title: "OCR Accuracy & Language Support",
    description: "Significant improvements to our OCR engine, increasing accuracy for low-resolution scans and adding support for 5 new languages.",
    changes: [
      { type: "Improvement", text: "OCR accuracy improved by an average of 8% for documents under 150 DPI." },
      { type: "New", text: "Added OCR support for Spanish, French, German, Italian, and Portuguese." },
      { type: "Fix", text: "Fixed a rare issue with table detection in multi-column PDF layouts." },
    ],
    tags: ["OCR", "AI", "Internationalization"]
  },
];

const changeTypeIcons = {
  "New": <PlusCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />,
  "Improvement": <Zap className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0" />,
  "Fix": <Bug className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />,
  "Security": <ShieldCheck className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" />,
  "Breaking": <GitMerge className="w-5 h-5 text-purple-500 mr-2 flex-shrink-0" />,
};

const ChangelogPage = () => {
  const location = useLocation();
  const pageTitle = "Changelog | WebFiny Product Updates";
  const pageDescription = "Stay informed about the latest features, improvements, and bug fixes for WebFiny's PDF APIs and document automation platform.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const changelogSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": canonicalUrl,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": changelogData.map((entry, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "SoftwareApplication",
          "name": `WebFiny API ${entry.version}`,
          "softwareVersion": entry.version,
          "datePublished": entry.date,
          "releaseNotes": `${canonicalUrl}#${entry.version}`,
          "description": entry.description,
          "applicationCategory": "DeveloperApplication",
          "operatingSystem": "Cross-platform", 
          "offers": {
            "@type": "Offer",
            "price": "0", 
            "priceCurrency": "USD"
          },
          "publisher": {
            "@type": "Organization",
            "name": "WebFiny"
          }
        }
      }))
    }
  };


  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[changelogSchema]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <GitMerge className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              Product Changelog
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover the latest updates, features, and improvements to the WebFiny platform. We're constantly working to make our PDF APIs better for you.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {changelogData.map((entry, index) => (
              <motion.div
                key={entry.version}
                id={entry.version}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="mb-12 p-6 md:p-8 bg-card rounded-xl shadow-xl relative scroll-mt-20"
              >
                <div className="absolute -top-5 -left-5 p-3 bg-primary rounded-full shadow-lg text-primary-foreground">
                  {entry.icon}
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    {entry.title}
                  </h2>
                  <span className="text-sm text-muted-foreground mt-2 sm:mt-0 bg-muted px-2 py-1 rounded-md">{new Date(entry.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="flex items-center space-x-3 mb-4 text-sm">
                  <span className={`font-semibold px-3 py-1 rounded-full text-xs ${
                    entry.type === "Major Version Release" ? "bg-purple-500/20 text-purple-400" :
                    entry.type === "Feature Release" ? "bg-green-500/20 text-green-400" :
                    entry.type === "Improvement Release" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-blue-500/20 text-blue-400"
                  }`}>
                    {entry.type}
                  </span>
                  <span className="text-muted-foreground">Version: {entry.version}</span>
                </div>

                <p className="text-muted-foreground mb-6">{entry.description}</p>

                <h3 className="text-lg font-semibold text-foreground mb-3">Key Changes:</h3>
                <ul className="space-y-2 mb-6">
                  {entry.changes.map((change, idx) => (
                    <li key={idx} className="flex items-start">
                      {changeTypeIcons[change.type]}
                      <span className="text-muted-foreground"><strong className="text-foreground/90">{change.type}:</strong> {change.text}</span>
                    </li>
                  ))}
                </ul>

                {entry.tags && entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {entry.tags.map(tag => (
                      <span key={tag} className="text-xs bg-accent/20 text-accent-foreground px-2 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangelogPage;